import React from "react";
import Parse from "parse";

import { useTranslation, AdminLayout, useForceRender } from "@opendash/core";
import { AdminToolbar } from "@opendash/ui";

import { Button, Table } from "antd";
import type { TableColumnType } from "antd";

import { useParseQuery } from "parse-hooks";
import { GlossaryEntry, Language } from "../types";
import { Icon } from "@opendash/icons";

import { DEFAULT_TENANT_ID } from "../const";
import { $parse } from "@opendash/plugin-parse";

export const GlossaryAdminRoute = React.memo(function GlossaryAdminRoute() {
  const t = useTranslation();
  const forceRender = useForceRender();
  const [search, onSearch] = React.useState("");

  const currentTenant = Parse.User.current()?.get("tenant");

  const entriesQuery = React.useMemo(
    () =>
      new Parse.Query(GlossaryEntry)
        .equalTo("tenant", currentTenant)
        .ascending("keyword")
        .limit(1000000),
    [currentTenant?.id]
  );

  const defaultEntriesQuery = React.useMemo(
    () =>
      new Parse.Query(GlossaryEntry)
        .equalTo(
          "tenant",
          new Parse.Object("OD3_Tenant", { id: DEFAULT_TENANT_ID })
        )
        // .ascending("title")
        .limit(1000000),
    []
  );

  const entries = useParseQuery(entriesQuery);
  const defaultEntries = useParseQuery(defaultEntriesQuery);

  const { rows, languages } = React.useMemo(() => {
    const map: Record<string, Record<string, GlossaryEntry>> = {};
    const languages: string[] = [];

    const all = [...defaultEntries.result, ...entries.result];

    for (const e of all) {
      const lang = e.get("language")?.id;
      const keyword = e.get("keyword");

      if (!map[keyword]) {
        map[keyword] = {};
      }

      if (!languages.includes(lang)) {
        languages.push(lang);
      }

      map[keyword][lang] = e;
    }

    const rows = Object.entries(map).map(([keyword, translations]) => {
      return {
        keyword,
        translations,
      };
    });

    return {
      languages,
      rows,
    };
  }, [defaultEntries.result, entries.result]);

  const cols = React.useMemo(() => {
    type RowType = (typeof rows)[0];
    type ColType = TableColumnType<RowType>;

    const cols: ColType[] = [
      // {
      //   title: t("app:admin.glossary.col_keyword"),
      //   key: "keyword",
      //   render: (value, row) => {
      //     return row.keyword;
      //   },
      // },

      ...languages.map((language, i) => {
        const col: ColType = {
          title: language,
          key: `language~${language}`,
          dataIndex: language,
          width: 100 / languages.length + "%",

          sorter: (a, b) => {
            const titleA = a.translations[language]?.get("title") || "";
            const titleB = b.translations[language]?.get("title") || "";

            return titleA.localeCompare(titleB, language);
          },

          sortDirections: ["ascend", "descend", "ascend"],

          ...(i === 0 && {
            defaultSortOrder: "ascend",
          }),

          render: (value, row) => {
            const translation = row.translations[language];

            if (
              translation &&
              translation.get("tenant")?.id !==
                Parse.User.current()?.get("tenant")?.id
            ) {
              return (
                <TableCell
                  title={translation.get("title")}
                  actionLabel={t("app:admin.glossary.action_overwrite")}
                  actionIcon="fa:edit"
                  actionHandler={() => {
                    const entry = new GlossaryEntry({
                      keyword: row.keyword,
                      language: new Language({ id: language }),
                      title: translation.get("title"),
                      text: translation.get("text"),
                    });

                    $parse.ui
                      .createObject(entry.className, entry, ["title", "text"])
                      .then((saved) => {
                        if (saved) {
                          entries.reload();
                        }
                      });
                  }}
                />
              );
            }

            if (translation) {
              return (
                <TableCell
                  title={translation.get("title")}
                  actionLabel={t("app:admin.glossary.action_edit")}
                  actionIcon="fa:edit"
                  actionHandler={() => {
                    $parse.ui
                      .updateObject(translation.className, translation.id, [
                        "title",
                        "text",
                      ])
                      .then((saved) => {
                        if (saved) {
                          entries.reload();
                        }
                      });
                  }}
                  deleteLabel={t("app:admin.glossary.action_delete")}
                  deleteHandler={() => {
                    $parse.ui
                      .deleteObject(translation.className, translation.id)
                      .then((deleted) => {
                        if (deleted) {
                          Promise.resolve(entries.reload()).then(() => {
                            forceRender();
                          });
                        }
                      });
                  }}
                />
              );
            }

            return (
              <TableCell
                warning
                title={t("app:admin.glossary.cell_empty")}
                actionLabel={t("app:admin.glossary.action_create")}
                actionIcon="fa:plus"
                actionPrimary
                actionHandler={() => {
                  const entry = new GlossaryEntry({
                    keyword: row.keyword,
                    language: new Language({ id: language }),
                  });
                  $parse.ui
                    .createObject(entry.className, entry, ["title", "text"])
                    .then((saved) => {
                      if (saved) {
                        entries.reload();
                      }
                    });
                }}
              />
            );
          },
        };

        return col;
      }),
    ];

    return cols;
  }, [languages]);

  const rowsAfterSearch = React.useMemo(() => {
    if (!search) {
      return rows;
    }

    const s = search.toLocaleLowerCase();

    return rows.filter((row) => {
      for (const translation of Object.values(row.translations)) {
        if (translation.get("title")?.toLocaleLowerCase().includes(s)) {
          return true;
        }

        // if (translation.get("text")?.toLocaleLowerCase().includes(s)) {
        //   return true;
        // }
      }

      return false;
    });
  }, [rows, search]);

  return (
    <AdminLayout contentPadding>
      <AdminToolbar
        title={t("app:admin.glossary.title")}
        description={t("app:admin.glossary.description")}
        padding={false}
        search={search}
        onSearch={onSearch}
        onBackButton={
          search
            ? () => {
                onSearch("");
              }
            : undefined
        }
        actions={[
          <Button
            key="new"
            type="primary"
            icon={<Icon icon="fa:plus" />}
            children={t("app:admin.glossary.action_new")}
            onClick={async () => {
              try {
                const id = await $parse.ui.createObject("APP_GlossaryEntry");
                await entries.reload();
                const entry = await new Parse.Query("APP_GlossaryEntry").get(
                  id
                );

                if (entry) {
                  onSearch(entry.get("title"));
                }
              } catch (error) {}
            }}
          />,
        ]}
      />
      <div className="od-page-main">
        {cols.length > 0 && (
          <Table
            rowKey="id"
            loading={entries.loading && defaultEntries.loading}
            dataSource={rowsAfterSearch}
            columns={cols}
            pagination={false}
          />
        )}
      </div>
      {/* <List
        loading={entries.loading}
        dataSource={entries.result}
        renderItem={(entry) => {
          return (
            <List.Item>
              <>
                <List.Item.Meta
                  title={
                    <span>
                      {entry.get("keyword")}{" "}
                      <Tag>{entry.get("language")?.id}</Tag>
                    </span>
                  }
                  description={entry.get("title")}
                />

                <Space direction="horizontal">
                  <Button
                    key="edit"
                    type="primary"
                    icon={<Icon icon="fa:edit" />}
                    children={t("app:admin.glossary.action_edit")}
                    onClick={() => {
                      $parse.ui.updateObject(entry.className, entry.id).then((saved) => {
                        if (saved) {
                          entries.reload();
                        }
                      });
                    }}
                  />

                  <Button
                    key="delete"
                    type="default"
                    danger
                    icon={<Icon icon="fa:trash" />}
                    children={t("app:admin.glossary.action_delete")}
                    onClick={() => {
                      $parse.ui.deleteObject(entry.className, entry.id).then(
                        (deleted) => {
                          if (deleted) {
                            entries.reload();
                          }
                        }
                      );
                    }}
                  />
                </Space>
              </>
            </List.Item>
          );
        }}
      /> */}
    </AdminLayout>
  );
});

interface TableCellProps {
  title: string | React.ReactNode;
  warning?: boolean;
  actionLabel: string | React.ReactNode;
  actionIcon: string;
  actionHandler(): void;
  actionPrimary?: boolean;
  deleteLabel?: string;
  deleteHandler?(): void;
}

function TableCell({
  title,
  actionLabel,
  actionIcon,
  actionHandler,
  actionPrimary = false,
  warning = false,
  deleteLabel,
  deleteHandler,
}: TableCellProps) {
  return (
    <div>
      <span
        style={{
          display: "block",
          marginBottom: 10,
          paddingBottom: 3,
          borderBottom: "1px solid rgba(0,0,0,.06)",
        }}
      >
        {warning && (
          <Icon icon="fa:exclamation-triangle" style={{ marginRight: 10 }} />
        )}
        {title}
      </span>
      <Button
        size="small"
        type={actionPrimary ? "primary" : "default"}
        icon={<Icon icon={actionIcon} />}
        children={actionLabel}
        onClick={actionHandler}
      />

      {deleteLabel && deleteHandler && (
        <Button
          style={{ marginLeft: 10 }}
          size="small"
          type="default"
          danger
          icon={<Icon icon="fa:trash-alt" />}
          children={deleteLabel}
          onClick={deleteHandler}
        />
      )}
    </div>
  );
}
